import { useCallback, useMemo } from 'react'

import type { ErrorLike } from '@apollo/client'
import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { ContentItemCard_ContentItem } from '~publish/components/ContentItemCard'
import {
  refreshConnectionTotalCount,
  refreshCountIfPagesUnloaded,
  useDebouncedTask,
} from '~publish/components/ContentItemCard/connectionRefresh'
import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import type {
  ContentItemUpsertEventType,
  RealTimeContentItem,
} from '~publish/components/ContentItemCard/useContentItemRealTimePusherUpdates'
import { useContentItemRealTimePusherUpdates } from '~publish/components/ContentItemCard/useContentItemRealTimePusherUpdates'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import type { UseIntersectionObserverReturn } from '~publish/hooks/useIntersectionObserver'
import { client } from '~publish/legacy/apollo-client'

const UNDATED_CONTENT_ITEM_DRAFTS_PAGE_SIZE = 20

export const GetUndatedContentItemDrafts = graphql(
  `
    query GetUndatedContentItemDrafts(
      $first: Int!
      $after: String
      $organizationId: OrganizationId!
    ) {
      contentItems(
        first: $first
        after: $after
        input: {
          organizationId: $organizationId
          filter: {
            contentStatus: draftContent
            targetDate: { presence: absent }
          }
        }
      ) {
        edges {
          node {
            id
            ...ContentItemCard_ContentItem
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        totalCount
      }
    }
  `,
  [ContentItemCard_ContentItem],
)

type GetUndatedContentItemDraftsData = ResultOf<
  typeof GetUndatedContentItemDrafts
>
type GetUndatedContentItemDraftsVariables = VariablesOf<
  typeof GetUndatedContentItemDrafts
>

type UseUndatedContentItemDraftsResult = {
  /** Channel-less drafts with no target date, newest first. */
  drafts: ContentItemDraft[]
  totalCount: number
  loading: boolean
  error: ErrorLike | undefined
  fetchingMore: boolean
  hasNextPage: boolean
  /** Attach to the last rendered draft to load the next page on scroll. */
  lastElementRef: UseIntersectionObserverReturn[0]
}

/**
 * Channel-less drafts without a target date, for the calendar's No Date
 * sidebar. Mirrors the undated posts query (`dueAtPresence: absent`) but on the
 * `contentItems` connection filtered to `draftContent` + `presence: absent`,
 * and paginates via infinite scroll like the drafts list — the sidebar can hold
 * more than one page of drafts.
 */
export function useUndatedContentItemDrafts({
  skip = false,
}: { skip?: boolean } = {}): UseUndatedContentItemDraftsResult {
  const organizationId = useOrganizationId()

  const { data, error, fetchMore, networkStatus, refetch, variables } =
    useQuery(GetUndatedContentItemDrafts, {
      variables: {
        first: UNDATED_CONTENT_ITEM_DRAFTS_PAGE_SIZE,
        after: null,
        organizationId: organizationId || '',
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
      skip: skip || !organizationId,
    })

  const scheduleRefetch = useDebouncedTask(refetch, 'undated drafts refetch')
  const scheduleCountRefresh = useDebouncedTask(
    () => refreshConnectionTotalCount(GetUndatedContentItemDrafts, variables),
    'undated drafts count refresh',
  )

  useContentItemRealTimePusherUpdates({
    skip: skip || !organizationId,
    onItemFetched: (item, eventType) =>
      upsertUndatedContentItemDraft(variables, item, eventType, {
        scheduleRefetch,
        scheduleCountRefresh,
      }),
    onDeleted: (id) =>
      removeUndatedContentItemDraft(variables, id, scheduleCountRefresh),
  })

  const loading = networkStatus === NetworkStatus.loading
  const fetchingMore = networkStatus === NetworkStatus.fetchMore
  const hasNextPage = data?.contentItems.pageInfo.hasNextPage ?? false
  const endCursor = data?.contentItems.pageInfo.endCursor

  const loadMore = useCallback(async () => {
    try {
      await fetchMore({
        variables: { after: endCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.contentItems.edges) return prev

          const newEdges = fetchMoreResult.contentItems.edges
          const newIds = new Set(newEdges.map((edge) => edge.node.id))
          const existingEdges = (prev.contentItems.edges ?? []).filter(
            (edge) => !newIds.has(edge.node.id),
          )

          return {
            ...prev,
            contentItems: {
              ...prev.contentItems,
              edges: [...existingEdges, ...newEdges],
              pageInfo: fetchMoreResult.contentItems.pageInfo,
            },
          }
        },
      })
    } catch (caughtError) {
      const message =
        caughtError instanceof Error ? caughtError.message : 'Unknown error'
      toast.error('Error fetching more drafts')
      throw new Error(`Failed to fetch more undated drafts: ${message}`, {
        cause: caughtError,
      })
    }
  }, [fetchMore, endCursor])

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage,
    fetchMore: loadMore,
  })

  const drafts = useMemo(() => {
    const nodes = (data?.contentItems.edges ?? []).map((edge) => edge.node)
    return [...readFragment(ContentItemCard_ContentItem, nodes)]
  }, [data?.contentItems.edges])

  return {
    drafts,
    totalCount: data?.contentItems.totalCount ?? 0,
    loading,
    error,
    fetchingMore,
    hasNextPage,
    lastElementRef,
  }
}

type UndatedDraftsRefreshers = {
  scheduleRefetch: () => void
  scheduleCountRefresh: () => void
}

/**
 * Inserts, updates, or drops a content item in the undated-drafts cache
 * entry.
 */
function upsertUndatedContentItemDraft(
  variables: GetUndatedContentItemDraftsVariables | undefined,
  item: RealTimeContentItem,
  eventType: ContentItemUpsertEventType,
  { scheduleRefetch, scheduleCountRefresh }: UndatedDraftsRefreshers,
): void {
  const existingData = client.cache.readQuery<GetUndatedContentItemDraftsData>({
    query: GetUndatedContentItemDrafts,
    variables,
  })
  if (!existingData?.contentItems.edges) {
    return
  }

  const wasPresent = existingData.contentItems.edges.some(
    (edge) => edge.node.id === item.id,
  )
  const remainingEdges = existingData.contentItems.edges.filter(
    (edge) => edge.node.id !== item.id,
  )

  // Only a channel-less draft with no target date belongs in this sidebar.
  const unmaskedItem = readFragment(ContentItemCard_ContentItem, item)
  const belongsHere =
    isOfType(unmaskedItem.body, 'DraftContent') &&
    unmaskedItem.targetDate === null

  if (!belongsHere) {
    // Refresh only the count: a full refetch would reset a scrolled list on
    // every edit to a dated draft.
    if (!wasPresent) {
      refreshCountIfPagesUnloaded(
        existingData.contentItems,
        scheduleCountRefresh,
      )
      return
    }
    client.cache.writeQuery({
      query: GetUndatedContentItemDrafts,
      variables,
      data: {
        ...existingData,
        contentItems: {
          ...existingData.contentItems,
          edges: remainingEdges,
          totalCount: existingData.contentItems.totalCount - 1,
        },
      },
    })
    return
  }

  // The list sorts newest-created first, so an update keeps the item's place.
  if (wasPresent) {
    client.cache.writeQuery({
      query: GetUndatedContentItemDrafts,
      variables,
      data: {
        ...existingData,
        contentItems: {
          ...existingData.contentItems,
          edges: existingData.contentItems.edges.map((edge) =>
            edge.node.id === item.id ? { ...edge, node: item } : edge,
          ),
        },
      },
    })
    return
  }

  // An update to an absent item gives neither its place in the list nor
  // whether `totalCount` already counts it: it may sit on an unloaded page,
  // or its target date was just cleared. Only a creation goes in at the head.
  if (eventType === 'updated') {
    scheduleRefetch()
    return
  }

  client.cache.writeQuery({
    query: GetUndatedContentItemDrafts,
    variables,
    data: {
      ...existingData,
      contentItems: {
        ...existingData.contentItems,
        edges: [
          { __typename: 'ContentItemEdge' as const, node: item },
          ...remainingEdges,
        ],
        totalCount: existingData.contentItems.totalCount + 1,
      },
    },
  })
}

/**
 * Drops a content item from the undated-drafts cache entry after a delete
 * event, adjusting `totalCount`.
 */
function removeUndatedContentItemDraft(
  variables: GetUndatedContentItemDraftsVariables | undefined,
  id: string,
  scheduleCountRefresh: () => void,
): void {
  const existingData = client.cache.readQuery<GetUndatedContentItemDraftsData>({
    query: GetUndatedContentItemDrafts,
    variables,
  })
  if (!existingData?.contentItems.edges) {
    return
  }

  const wasPresent = existingData.contentItems.edges.some(
    (edge) => edge.node.id === id,
  )
  if (!wasPresent) {
    refreshCountIfPagesUnloaded(existingData.contentItems, scheduleCountRefresh)
    return
  }

  client.cache.writeQuery({
    query: GetUndatedContentItemDrafts,
    variables,
    data: {
      ...existingData,
      contentItems: {
        ...existingData.contentItems,
        edges: existingData.contentItems.edges.filter(
          (edge) => edge.node.id !== id,
        ),
        totalCount: existingData.contentItems.totalCount - 1,
      },
    },
  })
}
